import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { priorityName } from "./format";
import type { IncidentAlerts } from "../api/types";

const POLL_MS = 30_000;
const SEEN_KEY = "patrol.alertsSeenAt";

function readSeen(): number {
  try {
    return Number(localStorage.getItem(SEEN_KEY) ?? 0);
  } catch {
    return 0;
  }
}

function writeSeen(t: number) {
  try {
    localStorage.setItem(SEEN_KEY, String(t));
  } catch {
    // Без хранилища уведомление просто покажется снова после перезагрузки.
  }
}

/**
 * Опрос новых инцидентов. Возвращает число необработанных — для счётчика
 * в меню — и показывает плашку о срочных, которых пользователь ещё не видел.
 * Это не «онлайн»: инцидент появляется, когда устройство его отправило.
 */
export function useIncidentAlerts(enabled: boolean) {
  const [alerts, setAlerts] = useState<IncidentAlerts | null>(null);
  const [fresh, setFresh] = useState<IncidentAlerts["urgent"]>([]);
  const seen = useRef(readSeen());

  useEffect(() => {
    if (!enabled) return;
    let stop = false;
    const tick = () => {
      api.get<IncidentAlerts>("/api/v1/incidents/alerts").then((a) => {
        if (stop) return;
        setAlerts(a);
        setFresh(a.urgent.filter((u) => new Date(u.received_at).getTime() > seen.current));
      }).catch(() => { /* сеть пропала — попробуем в следующий раз */ });
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { stop = true; clearInterval(id); };
  }, [enabled]);

  useEffect(() => {
    const base = document.title.replace(/^\(\d+\) /, "");
    document.title = alerts && alerts.new_count > 0 ? `(${alerts.new_count}) ${base}` : base;
  }, [alerts]);

  const dismiss = () => {
    const newest = Math.max(seen.current, ...fresh.map((f) => new Date(f.received_at).getTime()));
    seen.current = newest;
    writeSeen(newest);
    setFresh([]);
  };

  return { newCount: alerts?.new_count ?? 0, fresh, dismiss };
}

export function AlertToast({ fresh, dismiss }: { fresh: IncidentAlerts["urgent"]; dismiss: () => void }) {
  if (fresh.length === 0) return null;
  const top = fresh[0]!;
  return (
    <div className="toast" role="alert">
      <div>
        <strong>{fresh.length === 1 ? "Новый срочный инцидент" : `Новых срочных инцидентов: ${fresh.length}`}</strong>
        <div className="small">{priorityName(top.priority)} · {top.category_name} · {top.territory_name}</div>
      </div>
      <div className="filters" style={{ margin: 0 }}>
        <Link className="button" to={`/incidents?id=${top.id}`} onClick={dismiss}>Открыть</Link>
        <button className="secondary" onClick={dismiss}>Скрыть</button>
      </div>
    </div>
  );
}
